import { ApiLocation } from "@incendium/api";
import { Autocomplete, TextField, OutlinedTextFieldProps } from "@mui/material";
import { useLocations } from "Hooks";
import { useMemo } from "react";
import {
  TextValidator,
  ValidatorComponentProps,
} from "react-material-ui-form-validator";

interface ILocationDropdownProps
  extends Omit<OutlinedTextFieldProps, "onChange"> {
  value?: number;
  onChange: (n?: number, l?: ApiLocation) => void;
  showInAnalytics?: boolean;
  validators?: ValidatorComponentProps["validators"];
  errorMessages?: ValidatorComponentProps["errorMessages"];
  name?: string;
}

function LocationDropdown({
  label,
  size,
  variant,
  value,
  onChange,
  fullWidth,
  sx,
  showInAnalytics,
  validators,
  errorMessages,
  error,
  helperText,
  name,
  ...rest
}: ILocationDropdownProps) {
  const { locations } = useLocations();

  const filteredLocations = useMemo(() => {
    return locations.filter((l) =>
      showInAnalytics ? l.showInAnalytics === showInAnalytics : true
    );
  }, [locations, showInAnalytics]);

  const selectLocation = useMemo(
    () => (locations || []).find((l) => l.id === value) || null,
    [locations, value]
  );

  return (
    <Autocomplete
      sx={sx}
      fullWidth={fullWidth}
      options={filteredLocations}
      value={selectLocation}
      onChange={(e, v) => {
        onChange(v?.id, v ? v : undefined);
      }}
      getOptionLabel={(o: ApiLocation) => o.name || ""}
      renderInput={(params) => {
        if (validators) {
          // Use TextValidator when validation is needed
          return (
            <TextValidator
              {...params}
              name={name || "location"}
              value={selectLocation ? String(selectLocation.id) : ""}
              validators={validators}
              errorMessages={errorMessages}
              label={label}
              variant={variant}
              size={size}
              onChange={() => { }} // onChange is handled by Autocomplete
            />
          );
        }

        // Use regular TextField when no validation is needed
        return (
          <TextField
            {...params}
            {...rest}
            label={label}
            variant={variant}
            size={size}
            error={error}
            helperText={helperText}
          />
        );
      }}
    />
  );
}

export default LocationDropdown;
